import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setUrlData, View } from "features/global/reducer";
import { Tabs, Tab, Paper } from "@material-ui/core";
import { Club, IncreasedDataProtections } from "api/clubs";
import { ClubSelect, HelpscoutFab } from "components";
import { getClub } from "api/admin/clubs";
import { Membership } from "api/memberships";
import { postConfirmConnectedAccount } from "api/owner/stripe";
import { useSnackbar } from "notistack";
import { UserProfile } from "api/user";
import i18n from "./i18n";
import GeneralTab from "./GeneralTab";
import BookingsTab from "./BookingsTab";
import MembersTab from "./MembersTab";
import OwnershipTab from "./OwnershipTab";
import SubscriptionTab from "./SubscriptionTab";
import "./clubStyle.scss";
// prop types
interface ClubViewProps {
    club: Club | null,
    currentTab: number,
    memberships: Membership[],
    urlData: { [x: string]: string },
    userProfile: UserProfile | null,
    onFetchUserProfile: () => void,
    onSetCurrentTab: (tab: number) => void,
    onNavigateTo: ({ view, tab }: { view: View, tab?: number }) => void,
}

// club-related admin stuff
const ClubView = ({ club: currentClub, currentTab, memberships, urlData, userProfile, onFetchUserProfile, onSetCurrentTab, onNavigateTo }: ClubViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // ui state
    const [club, setClub] = useState<Club>();

    // stripe redirect confirm account
    const confirmStripeAccount = async () => {
        try {
            enqueueSnackbar(i18n.connectingWithStripe, { variant: "info" });
            const { code, state } = urlData;
            await postConfirmConnectedAccount({ code, state });
            dispatch(setUrlData({})); // clear data
            enqueueSnackbar(i18n.connectWithStripeSuccess, { variant: "success" });
        } catch (error: any) {
            enqueueSnackbar(`${i18n.anErrorOccurred}: ${error.description} (${error.type})`, { variant: "error" });
        }
    };
    useEffect(() => { if (urlData.code && urlData.state) confirmStripeAccount(); }, [urlData]); // eslint-disable-line

    // reset ui
    const fetchClub = async (clubId: string) => setClub(await getClub({ clubId }));
    useEffect(() => { if (currentClub) fetchClub(currentClub!.id); }, [currentClub]);

    // render
    const userRole = memberships.find((m) => m.club.id === club?.id)?.userRole;
    const isOwner = userRole === "owner";
    const isCompany = club?.capabilities.includes("company");
    const isRootUser = userProfile?.userTypes?.includes("root-management");
    return (
        <>
            <HelpscoutFab club={club} />
            <div className="headerDiv">
                <Paper className="headerDiv">
                    {/* tabs */}
                    <Tabs
                        indicatorColor="primary"
                        textColor="primary"
                        value={currentTab}
                        className="tabsClass"
                        onChange={(e, index) => onSetCurrentTab(index)}
                    >
                        <Tab className="tabClass" label={i18n.general} />
                        <Tab className="tabClass" label={i18n.bookings} />
                        <Tab className="tabClass" label={i18n.members} />
                        {isOwner && <Tab className="tabClass" label={i18n.ownership} />}
                        {isOwner && isCompany && (!club?.increasedDataProtections?.includes(IncreasedDataProtections.SUBSCRIPTION) || isRootUser) && (
                            <Tab className="tabClass" disabled={false} label={i18n.subscription} />
                        )}
                    </Tabs>

                    {/* club select */}
                    <ClubSelect
                        clubId={club?.id}
                        controlProps={{ className: "clubSelect" }}
                        hideAll
                        userRoles={(currentTab === 3) ? ["owner"] : ["owner", "admin"]} // ownership tab
                        onChange={(id) => fetchClub(id!)}
                    />
                </Paper>
            </div>
            {currentTab === 0 && <GeneralTab club={club} onFetchUserProfile={onFetchUserProfile} />}
            {currentTab === 1 && <BookingsTab club={club} onFetchUserProfile={onFetchUserProfile} />}
            {currentTab === 2 && <MembersTab club={club} onFetchUserProfile={onFetchUserProfile} />}
            {currentTab === 3 && isOwner && <OwnershipTab club={club} onFetchUserProfile={onFetchUserProfile} />}
            {currentTab === 4 && isOwner && isCompany && club && (<SubscriptionTab club={club} />)}
        </>
    );
};

export default ClubView;
